// @ts-nocheck
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Menu, MenuItem, styled, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
// Import SVG flags
import enFlag from "../../assets/flags/en-flag.svg";
import frFlag from "../../assets/flags/fr-flag.svg";
import esFlag from "../../assets/flags/es-flag.svg";

// Styled components
const LanguageSelectorContainer = styled(Box)(({ align }) => ({
  color: "black",
  display: "flex",
  justifyContent: align === "left" ? "flex-start" : "center",
  width: "100%",
}));

const LanguageButton = styled(Button)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  border: "1px solid rgba(0, 0, 0, 0.1)",
  borderRadius: 8,
  padding: "8px 12px",
  transition: "all 0.2s ease",
  boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 1)",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
  },
  color: "black",
}));

const FlagImage = styled("img")({
  width: 24,
  height: 18,
  marginRight: 8,
});

const LanguageText = styled(Typography)(({ theme }) => ({
  marginLeft: 8,
  fontSize: 14,
  fontWeight: 500,
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 8,
    marginTop: -8,
    minWidth: 160,
    boxShadow: "0 5px 15px rgba(0, 0, 0, 0.15)",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
  },
}));

const StyledMenuItem = styled(MenuItem, { shouldForwardProp: (prop) => prop !== "active" })<{
  active?: boolean;
}>(({ theme, active }) => ({
  display: "flex",
  alignItems: "center",
  padding: "10px 12px",
  ...(active && {
    backgroundColor: "rgba(0, 0, 0, 0.08)",
    fontWeight: 500,
  }),
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.05)",
  },
}));

interface LanguageSelectorProps {
  className?: string;
  align?: "left" | "center";
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ className, align = "center" }) => {
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const currentLanguage = i18n.language;

  // Get flag image based on language code
  const getFlagImage = (langCode: string) => {
    switch (langCode) {
      case "en":
        return enFlag;
      case "fr":
        return frFlag;
      case "es":
        return esFlag;
      default:
        return enFlag;
    }
  };

  // Get language name based on language code
  const getLanguageName = (langCode: string) => {
    switch (langCode) {
      case "en":
        return "English";
      case "fr":
        return "Français";
      case "es":
        return "Español";
      case "ar":
        return "العربية";
      default:
        return "English";
    }
  };

  // Handle menu open
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle menu close
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handle language change
  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    if (typeof window !== 'undefined') {
      localStorage.setItem('tgc-language', langCode);
    }
    handleClose();
  };

  return (
    <LanguageSelectorContainer className={className} align={align}>
      <LanguageButton
        id="language-selector-button"
        aria-controls={open ? "language-selector-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
      >
        <FlagImage src={getFlagImage(currentLanguage)} alt={getLanguageName(currentLanguage)} />
      </LanguageButton>

      <StyledMenu
        id="language-selector-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "language-selector-button",
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        {["en", "fr", "es"].map((langCode) => (
          <StyledMenuItem
            key={langCode}
            onClick={() => changeLanguage(langCode)}
            active={currentLanguage === langCode}
          >
            <FlagImage src={getFlagImage(langCode)} alt={getLanguageName(langCode)} />
            <Typography variant="body2">{getLanguageName(langCode)}</Typography>
          </StyledMenuItem>
        ))}
      </StyledMenu>
    </LanguageSelectorContainer>
  );
};

export default LanguageSelector;
